import type { SectionLocalExtents } from '@viewer/store/sectionSlice'
import { coordAxisLabels, type CoordFrame } from '@viewer/measure/coordReadout'

// ---------------------------------------------------------------------------
// Eixos do painel da Section box, como o utilizador os lê.
//
// O estado da caixa (`SectionLocalExtents`) vive no referencial do three:
// x = Este, y = Cima, z = -Norte. Mostrá-lo tal e qual punha a cota no
// "Eixo Y" e o Norte, com o sinal trocado, no "Eixo Z" (num modelo
// georreferenciado lia-se Z = -164025). Aqui só muda a APRESENTAÇÃO e a
// entrada de valores, com a mesma convenção do HUD de coordenadas
// (`measure/coordReadout.ts`): E, N e Cota quando há georreferenciação;
// X, Y e Z do ficheiro quando não há.
//
// O Norte é o z do three com o sinal trocado. Por isso o mínimo do Norte
// é o simétrico do MÁXIMO de z, e o máximo do Norte o simétrico do
// mínimo de z: sem esta troca, os campos min/max apareciam ao contrário.
//
// Numa caixa rodada (alinhada a uma face) os eixos são os locais da
// própria caixa, sem relação com o Norte, e ficam como estavam.
//
// Puro: números e rótulos, sem DOM nem three.
// ---------------------------------------------------------------------------

/** Eixo do estado interno (referencial three, ou local da caixa). */
export type EixoInterno = 'X' | 'Y' | 'Z'

export interface EixoApresentado {
  /** Eixo do estado interno que este campo controla. */
  interno: EixoInterno
  /** Rótulo mostrado. */
  rotulo: string
  /** true quando o valor mostrado é o simétrico do interno (Norte = -z). */
  invertido: boolean
  /** Nota curta ao lado do rótulo (ex.: "normal" na caixa rodada). */
  nota?: string
}

/** Os três campos do painel, pela ordem em que se mostram. */
export function eixosApresentados(
  frame: CoordFrame,
  alinhadaAoMundo: boolean,
): EixoApresentado[] {
  if (!alinhadaAoMundo) {
    return [
      { interno: 'X', rotulo: 'Local X', invertido: false },
      { interno: 'Y', rotulo: 'Local Y', invertido: false },
      { interno: 'Z', rotulo: 'Local Z', invertido: false, nota: 'normal' },
    ]
  }
  const [a, b, c] = coordAxisLabels(frame)
  return [
    { interno: 'X', rotulo: a, invertido: false },
    { interno: 'Z', rotulo: b, invertido: true },
    { interno: 'Y', rotulo: c, invertido: false },
  ]
}

function semMenosZero(n: number): number {
  return n === 0 ? 0 : n
}

/** Intervalo [min, max] de um eixo como se mostra. */
export function intervaloApresentado(
  e: SectionLocalExtents,
  eixo: EixoApresentado,
): { min: number; max: number } {
  const lo = e[`min${eixo.interno}` as keyof SectionLocalExtents]
  const hi = e[`max${eixo.interno}` as keyof SectionLocalExtents]
  return eixo.invertido
    ? { min: semMenosZero(-hi), max: semMenosZero(-lo) }
    : { min: lo, max: hi }
}

/** Aplica um valor escrito (ou arrastado) no campo `lado` do eixo
 *  apresentado e devolve os extents internos novos. Mantém min <= max,
 *  tal como antes. */
export function aplicarValorApresentado(
  e: SectionLocalExtents,
  eixo: EixoApresentado,
  lado: 'min' | 'max',
  valor: number,
): SectionLocalExtents {
  const next: SectionLocalExtents = { ...e }
  const minKey = `min${eixo.interno}` as keyof SectionLocalExtents
  const maxKey = `max${eixo.interno}` as keyof SectionLocalExtents
  // No eixo invertido, o min mostrado é o max interno (e vice-versa).
  const ladoInterno = eixo.invertido ? (lado === 'min' ? 'max' : 'min') : lado
  const v = eixo.invertido ? semMenosZero(-valor) : valor
  if (ladoInterno === 'min') next[minKey] = Math.min(v, next[maxKey])
  else next[maxKey] = Math.max(v, next[minKey])
  return next
}
